import en from './en.json';
import { coverageOf } from './coverage';
import { resolvePlurals } from './plural';
export { resolvePlurals } from './plural';

/** Every language Magia Li can be shown in once its message file exists (src/i18n/<lang>.json). */
export type Lang = 'he' | 'en' | 'ru' | 'ar' | 'fr' | 'uk' | 'am' | 'es' | 'pt-BR' | 'zh-CN' | 'hi' | 'fil' | 'th';
export type MessageKey = keyof typeof en;

/**
 * Everything language-specific besides the message files. Adding a language = one entry
 * here + one JSON file (docs/roadmap.md). `locale` drives number and money formatting.
 * `into`: "into <this language>" as it follows the verb "translate" (the {into} in texts). `reviewed: false` = machine-translated (npm run translate) and not yet checked by a native
 * speaker: the language picker marks it with `betaNote`, in the language itself.
 */
export const LANGUAGES: Record<Lang, { locale: string; dir: 'rtl' | 'ltr'; nativeName: string; into: string; inLang: string; reviewed: boolean; betaNote?: string }> = {
  he: { locale: 'he-IL', dir: 'rtl', nativeName: 'עברית', into: 'לעברית', inLang: 'בעברית', reviewed: true },
  en: { locale: 'en-IL', dir: 'ltr', nativeName: 'English', into: 'into English', inLang: 'in English', reviewed: true },
  ru: { locale: 'ru-IL', dir: 'ltr', nativeName: 'Русский', into: 'на русский язык', inLang: 'по-русски', reviewed: false, betaNote: 'машинный перевод, бета' },
  // Latin digits, as on Israeli payslips (the default Arabic locale would print ٦٬٤٤٣٫٨٥).
  ar: { locale: 'ar-IL-u-nu-latn', dir: 'rtl', nativeName: 'العربية', into: 'إلى العربية', inLang: 'بالعربية', reviewed: false, betaNote: 'ترجمة آلية، نسخة تجريبية' },
  fr: { locale: 'fr-IL', dir: 'ltr', nativeName: 'Français', into: 'en français', inLang: 'en français', reviewed: false, betaNote: 'traduction automatique, bêta' },
  uk: { locale: 'uk-IL', dir: 'ltr', nativeName: 'Українська', into: 'українською', inLang: 'українською', reviewed: false, betaNote: 'машинний переклад, бета' },
  am: { locale: 'am-IL', dir: 'ltr', nativeName: 'አማርኛ', into: 'ወደ አማርኛ', inLang: 'በአማርኛ', reviewed: false, betaNote: 'በማሽን የተተረጎመ፣ ቤታ' },
  es: { locale: 'es-IL', dir: 'ltr', nativeName: 'Español', into: 'al español', inLang: 'en español', reviewed: false, betaNote: 'traducción automática, beta' },
  'pt-BR': { locale: 'pt-BR', dir: 'ltr', nativeName: 'Português (Brasil)', into: 'para o português', inLang: 'em português', reviewed: false, betaNote: 'tradução automática, beta' },
  // Inbox 7 (2026-10-16): offered once their files are complete (translate on hold until the UI text review is approved).
  'zh-CN': { locale: 'zh-CN', dir: 'ltr', nativeName: '中文（简体）', into: '成中文', inLang: '用中文', reviewed: false, betaNote: '机器翻译，测试版' },
  // Latin digits, as on Israeli payslips.
  hi: { locale: 'hi-IN-u-nu-latn', dir: 'ltr', nativeName: 'हिन्दी', into: 'हिन्दी में', inLang: 'हिन्दी में', reviewed: false, betaNote: 'मशीनी अनुवाद, बीटा' },
  fil: { locale: 'fil-PH', dir: 'ltr', nativeName: 'Filipino', into: 'sa Filipino', inLang: 'sa Filipino', reviewed: false, betaNote: 'makina ang nagsalin, beta' },
  th: { locale: 'th-TH-u-nu-latn', dir: 'ltr', nativeName: 'ไทย', into: 'เป็นภาษาไทย', inLang: 'เป็นภาษาไทย', reviewed: false, betaNote: 'แปลโดยเครื่อง, เบต้า' },
};

/**
 * Hebrew (the default) and English (the fallback for any missing text) are in the main bundle; every
 * other language is its own file, loaded when chosen (`loadLanguage`), so the start screen stays small.
 * All are precached, so switching works offline.
 */
const EAGER = import.meta.glob<Record<string, string>>(['./he.json', './en.json'], { eager: true, import: 'default' });
const LAZY = import.meta.glob<Record<string, string>>(['./*.json', '!./he.json', '!./en.json'], { import: 'default' });

export const MESSAGES: Record<Lang, Record<string, string>> = Object.fromEntries(
  (Object.keys(LANGUAGES) as Lang[]).map((l) => [l, EAGER[`./${l}.json`] ?? {}]),
) as Record<Lang, Record<string, string>>;
MESSAGES.en = en;

/**
 * Texts of a feature in its own chunk (e.g. src/form106), merged when the chunk loads so the start screen stays small:
 * Hebrew and English by hand, other languages machine-translated when that language is in use (English shows for what
 * isn't). Never mutates the `en` file object, which language coverage is measured against.
 */
export function addMessages(lang: string, messages: Record<string, string>): void {
  if (!isLang(lang)) return;
  MESSAGES[lang] = { ...MESSAGES[lang], ...messages };
}

const LOADED = new Set<Lang>((Object.keys(LANGUAGES) as Lang[]).filter((l) => Object.keys(MESSAGES[l]).length > 0));

/** A language's messages, loaded once; English shows for anything not loaded yet. Feature texts added before stay. */
export async function loadLanguage(lang: Lang): Promise<void> {
  if (LOADED.has(lang)) return;
  const load = LAZY[`./${lang}.json`];
  if (load) MESSAGES[lang] = { ...(await load()), ...MESSAGES[lang] };
  LOADED.add(lang);
}
export async function loadAllLanguages(): Promise<void> {
  await Promise.all((Object.keys(LANGUAGES) as Lang[]).map(loadLanguage));
}

/**
 * A language is first offered once its file has every English key. A translation in progress (a run
 * stopped by the provider's quota) stays hidden until then; once complete, a language stays offered
 * when a later English change leaves keys untranslated, which show in English (`t`) until the next
 * `npm run translate` run (`npm run deploy` does not retranslate). Matt, 2026-10-14.
 */
export { MIN_COVERAGE } from './coverage';
export const coverage = (messages: Record<string, string> | undefined): number => coverageOf(messages, en);

/** The languages offered: complete now or once (coverage.ts isOffered), listed at build time (vite.config.ts). */
export const LANGS = (Object.keys(LANGUAGES) as Lang[]).filter((l) => l === 'he' || l === 'en' || __COMPLETE_LANGS__.includes(l));
export const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as string[]).includes(v);

/**
 * The language to start in when none was chosen yet: the first of the browser's languages the app
 * offers (he and its old code iw both mean Hebrew), else Hebrew.
 */
export function preferredLanguage(browser: readonly string[], offered: readonly Lang[] = LANGS): Lang {
  for (const tag of browser) {
    const base = tag.toLowerCase().split('-')[0] ?? '';
    const lang = base === 'iw' ? 'he' : base;
    // By the base language: any Portuguese browser (pt, pt-PT, pt-BR) gets pt-BR, the Portuguese offered.
    const match = offered.find((l) => l.toLowerCase() === lang || l.toLowerCase().split('-')[0] === lang);
    if (match) return match;
  }
  return 'he';
}

/** A language's name in the picker, in that language; a machine translation says so (`betaNote`). */
export const languageLabel = (lang: Lang): string => {
  const l = LANGUAGES[lang];
  return l.reviewed || !l.betaNote ? l.nativeName : `${l.nativeName} (${l.betaNote})`;
};

/** Message params holding agorot; formatted as shekels. */
const MONEY_PARAMS = new Set(['expected', 'actual', 'amount', 'uncertain', 'reserve']);

/**
 * An amount in agorot as text in the user's language. Hebrew keeps ₪ (currency style). Every other language writes its word for
 * shekels through the translated pattern `money.shekels` ("1,240 shekels", "1 240 шекелей"): the plural form by the language's own
 * rules (Intl.PluralRules, chosen with the decimals shown: "1.00 shekels"), the number by Intl.NumberFormat; a language without
 * the pattern yet shows the English word, never ₪. `whole`: a figure already rounded to shekels (the refund estimate, rounded
 * down to hundreds) shows no agorot (screen review #33).
 */
export function formatMoney(agorot: number, lang: Lang, opts: { whole?: boolean } = {}): string {
  return shekelText('money.shekels', lang, 'n', {}, agorot, opts.whole === true);
}

/** A range of amounts naming the currency once: "3,400 to 4,100 shekels" (`money.shekelsRange`); Hebrew "₪3,400–₪4,100". */
export function formatMoneyRange(lowAgorot: number, highAgorot: number, lang: Lang, opts: { whole?: boolean } = {}): string {
  if (lang === 'he') return `${formatMoney(lowAgorot, lang, opts)}–${formatMoney(highAgorot, lang, opts)}`;
  return shekelText('money.shekelsRange', lang, 'high', { low: lowAgorot }, highAgorot, opts.whole === true);
}

/** `others`: more amounts (agorot) in the pattern, by name, written as plain numbers; `plural`: the one the plural form follows. */
function shekelText(key: string, lang: Lang, plural: string, others: Record<string, number>, agorot: number, whole: boolean): string {
  const locale = LANGUAGES[lang].locale;
  const fd = whole ? 0 : 2;
  const digits = { minimumFractionDigits: fd, maximumFractionDigits: fd };
  if (lang === 'he') return new Intl.NumberFormat(locale, { style: 'currency', currency: 'ILS', currencyDisplay: 'narrowSymbol', ...digits }).format(agorot / 100);
  const own = MESSAGES[lang][key];
  const value = (a: number) => Number((a / 100).toFixed(fd));
  const shown = (a: number) => new Intl.NumberFormat(locale, digits).format(value(a));
  const template = own ?? MESSAGES.en[key] ?? '{n} shekels';
  const text = resolvePlurals(template, { [plural]: value(agorot) }, own === undefined ? 'en' : locale, fd)
    .replace(/\{(\w+)\}/g, (m, name: string) => (name === plural ? shown(agorot) : others[name] !== undefined ? shown(others[name]) : m));
  return own === undefined && LANGUAGES[lang].dir === 'rtl' ? `\u2066${text}\u2069` : text;
}

export function hasMessage(lang: Lang, key: string): boolean {
  return Object.hasOwn(MESSAGES[lang], key);
}

/**
 * Translates `key`, filling `{name}` placeholders and plural forms (`resolvePlurals`). Agorot params are formatted as money. A key a
 * language doesn't have yet (a machine translation not run since) shows the English text; unknown
 * keys show the key itself.
 */
/**
 * The key to show for the first mention of "payslip" on a screen: `<key>.first` carries the Hebrew word phonetically in
 * brackets ("payslip (tlush)"; machine-translated per docs/i18n/glossary-<lang>.md). Hebrew shows no gloss, and a language
 * whose `.first` text isn't translated yet shows the plain text.
 */
export const firstKey = (lang: Lang, key: string): string => (lang !== 'he' && MESSAGES[lang][`${key}.first`] !== undefined ? `${key}.first` : key);

/**
 * A number range ("12%–20%", "2013–2019", "3,400–4,100") in a right-to-left text: the dash between two numbers is a
 * neutral character, so the bidi algorithm lays the range out right to left ("20%–12%"). Isolated as left to right
 * (U+2066 … U+2069) it reads as written in every RTL language (screenshot review 2026-10-23 01:58).
 */
const NUMBER_RANGE = /[₪$]?\p{Nd}[\p{Nd}.,]*%?\s?[–—-]\s?[₪$]?\p{Nd}[\p{Nd}.,]*%?/gu;
export const isolateRanges = (text: string): string => text.replace(NUMBER_RANGE, (m) => `⁦${m}⁩`);

export function t(lang: Lang, key: string, given: Record<string, string | number> = {}): string {
  // {into}: the language's own "into <this language>" phrase, in the form that fits its grammar ("на русский язык").
  // {inLang}: the language's own whole phrase "in <this language>" (ru "по-русски"); a language name is never put into a sentence (docs/ui-glossary.md).
  const params: Record<string, string | number> = { into: LANGUAGES[lang].into, inLang: LANGUAGES[lang].inLang, ...given };
  const own = MESSAGES[lang][key];
  // The plural form by the rules of the language the text is in (English when it's the fallback).
  const rtl = LANGUAGES[lang].dir === 'rtl';
  const resolved = resolvePlurals(own ?? MESSAGES.en[key] ?? key, params, own === undefined ? 'en' : LANGUAGES[lang].locale);
  // Ranges written in the text itself only: values put in ({file}, {date}) are never touched ("2026-05" is not a range).
  const template = rtl && own !== undefined ? isolateRanges(resolved) : resolved;
  const text = template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    if (value === undefined) return match;
    if (typeof value === 'number' && MONEY_PARAMS.has(name)) return formatMoney(value, lang);
    return typeof value === 'number' ? value.toLocaleString(LANGUAGES[lang].locale) : value;
  });
  // English shown in a right-to-left language (not translated yet) is isolated as left to right, so its
  // punctuation and numbers stay in place ("Checking:", not ":Checking"; "6 days", not "days 6").
  return own === undefined && rtl ? `\u2066${text}\u2069` : text;
}
